'use client';

import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getWeekdayLabels, getMonthNames, buildCalendarDays, toLocalDateStr } from './calendarUtils';
import { t } from '@/locales';
import type { SlotAvailability } from '@/services/bookingService';

interface DateRangePickerGridProps {
  startDate: string;
  endDate: string;
  selectingEnd: boolean;
  onDayClick: (dateStr: string) => void;
  isDateBlocked: (dateStr: string) => boolean;
  priceMap: Record<string, { price: number; status: string; halfPrice?: number | null }>;
  basePrice: number | null;
  priceLoading: boolean;
  slotAvailability: Record<string, SlotAvailability>;
  showPrices: boolean;
  onMonthChange?: (year: number, month: number) => void;
  /** Tema selezione. 'classic' (default) = inizio verde / fine rosso (cliente).
   *  'violet' = giorni selezionati con contorno viola (usato dal wizard partner). */
  rangeTheme?: 'classic' | 'violet';
  /** Anteprima mezza-giornata sui giorni-bordo selezionati: metà PRENOTATA dal noleggio.
   *  { 'YYYY-MM-DD': 'morning' | 'afternoon' } → half-overlay viola sulla metà occupata. */
  partSelection?: Record<string, 'morning' | 'afternoon'>;
  /** Prezzo da mostrare sulla cella selezionata al posto di quello daily (es. blocco orario).
   *  { 'YYYY-MM-DD': prezzo }. Solo tema violet + celle selezionate. */
  priceOverrideByDate?: Record<string, number>;
}

export default function DateRangePickerGrid({
  startDate,
  endDate,
  selectingEnd,
  onDayClick,
  isDateBlocked,
  priceMap,
  basePrice,
  priceLoading,
  slotAvailability,
  showPrices,
  onMonthChange,
  rangeTheme = 'classic',
  partSelection,
  priceOverrideByDate,
}: DateRangePickerGridProps) {
  const violet = rangeTheme === 'violet';
  const [currentMonth, setCurrentMonth] = useState(() => {
    // Start on the month of startDate if set, otherwise current month
    if (startDate) {
      const d = new Date(startDate + 'T12:00:00');
      return { year: d.getFullYear(), month: d.getMonth() };
    }
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const todayStr = toLocalDateStr(new Date());

  const calendarDays = useMemo(
    () => buildCalendarDays(currentMonth.year, currentMonth.month),
    [currentMonth]
  );

  const weekdayLabels = getWeekdayLabels();
  const monthNames = getMonthNames();

  const isInRange = (dateStr: string) => {
    if (!startDate || !endDate) return false;
    return dateStr > startDate && dateStr < endDate;
  };

  // onMonthChange FUORI dall'updater di setState (chiamarlo dentro = setState del
  // parent durante il render → "Cannot update a component while rendering another").
  const prevMonth = () => {
    const next = currentMonth.month === 0
      ? { year: currentMonth.year - 1, month: 11 }
      : { year: currentMonth.year, month: currentMonth.month - 1 };
    setCurrentMonth(next);
    onMonthChange?.(next.year, next.month);
  };

  const nextMonth = () => {
    const next = currentMonth.month === 11
      ? { year: currentMonth.year + 1, month: 0 }
      : { year: currentMonth.year, month: currentMonth.month + 1 };
    setCurrentMonth(next);
    onMonthChange?.(next.year, next.month);
  };

  return (
    <div className="select-none">
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-3 px-1">
        <button
          onClick={prevMonth}
          className="p-2 rounded-xl hover:bg-slate-100 text-slate-600 transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <h3 className="text-lg font-black text-slate-900">
          {monthNames[currentMonth.month]} {currentMonth.year}
        </h3>
        <button
          onClick={nextMonth}
          className="p-2 rounded-xl hover:bg-slate-100 text-slate-600 transition-colors"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {weekdayLabels.map(day => (
          <div key={day} className="text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider py-1.5">
            {day}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map(({ date, day, isCurrentMonth }) => {
          const isPast = date < todayStr;
          const blocked = isDateBlocked(date);
          const isStart = date === startDate;
          const isEnd = date === endDate;
          const inRange = isInRange(date);
          const isToday = date === todayStr;

          // Price info
          const override = priceMap[date];
          const status = override?.status || 'AVAILABLE';
          const price = override?.price ?? basePrice;
          const isNotAvailable = status === 'NOT_AVAILABLE' || status === 'CLOSED';

          // Slot availability
          const slots = slotAvailability[date] || null;
          const isFullyBookedBySlots = slots ? !slots.morning && !slots.afternoon : false;
          const isPartial = slots && !isFullyBookedBySlots && (!slots.morning || !slots.afternoon);

          const disabled = isPast || blocked || isNotAvailable || isFullyBookedBySlots;

          // Cell styles
          let cellClasses = 'relative rounded-lg border p-1 min-h-[52px] md:min-h-[64px] transition-all duration-100 flex flex-col';

          if (!isCurrentMonth) {
            cellClasses += ' opacity-30 pointer-events-none border-transparent';
          } else if (violet && (isStart || isEnd)) {
            // Tema wizard: nessun verde/rosso, solo contorno viola = "selezionato".
            cellClasses += ' bg-violet-100 border-violet-500 text-violet-900 cursor-pointer ring-2 ring-violet-400 ring-offset-1';
          } else if (violet && inRange) {
            cellClasses += ' bg-violet-50 border-violet-200 text-violet-800 cursor-pointer';
          } else if (isStart) {
            cellClasses += ' bg-emerald-500 border-emerald-600 text-white cursor-pointer ring-2 ring-emerald-400 ring-offset-1';
          } else if (isEnd) {
            cellClasses += ' bg-red-500 border-red-600 text-white cursor-pointer ring-2 ring-red-400 ring-offset-1';
          } else if (inRange) {
            cellClasses += ' bg-blue-100 border-blue-200 text-blue-800 cursor-pointer';
          } else if (disabled) {
            cellClasses += isPast || !showPrices
              ? ' bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed'
              : ' bg-red-50 border-red-200 text-red-300 cursor-not-allowed';
          } else {
            cellClasses += ' bg-emerald-50/50 border-emerald-100 text-slate-700 cursor-pointer hover:bg-emerald-100 hover:border-emerald-200';
          }

          return (
            <div
              key={date}
              onClick={() => !disabled && isCurrentMonth && onDayClick(date)}
              className={cellClasses}
            >
              {/* Slot indicators for partial bookings */}
              {showPrices && isPartial && !isStart && !isEnd && !inRange && isCurrentMonth && (
                <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none flex flex-col">
                  <div className={`h-1/2 ${slots!.morning ? 'bg-emerald-50' : 'bg-red-50'}`} />
                  <div className={`h-1/2 ${slots!.afternoon ? 'bg-emerald-50' : 'bg-red-50'}`} />
                </div>
              )}

              {/* Anteprima mezza-giornata scelta nel wizard: la metà PRENOTATA dal noleggio
                  (viola pieno) vs la metà libera (trasparente). Sui giorni-bordo selezionati. */}
              {isCurrentMonth && partSelection?.[date] && (
                <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none flex flex-col">
                  <div className={`h-1/2 ${partSelection[date] === 'morning' ? 'bg-violet-400/45' : ''}`} />
                  <div className={`h-1/2 ${partSelection[date] === 'afternoon' ? 'bg-violet-400/45' : ''}`} />
                </div>
              )}

              {/* Day number + status dot */}
              <div className="flex items-center justify-between relative z-10">
                <span className={`text-xs font-bold leading-none ${
                  isToday && !isStart && !isEnd
                    ? 'bg-blue-600 text-white w-5 h-5 rounded-full flex items-center justify-center'
                    : ''
                }`}>
                  {day}
                </span>
                {showPrices && isCurrentMonth && !isPast && !isStart && !isEnd && (
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    blocked || isNotAvailable || isFullyBookedBySlots ? 'bg-red-400'
                    : isPartial ? 'bg-amber-400'
                    : 'bg-emerald-400'
                  }`} />
                )}
              </div>

              {/* Prezzo sulle celle PRENOTATE del wizard (violet + selezionate): nel caso
                  di mezza va nella METÀ prenotata (mattina=alto, pomeriggio=basso), intera=centro. */}
              {showPrices && isCurrentMonth && !isPast && violet && (isStart || isEnd || inRange) && (() => {
                const ovr = priceOverrideByDate?.[date]; // es. prezzo blocco orario
                const selPart = partSelection?.[date];
                const halfVal = override?.halfPrice != null && override.halfPrice > 0 ? override.halfPrice : price;
                const shown = ovr != null ? ovr : (selPart ? halfVal : price);
                if (shown == null) return null;
                const pos = selPart === 'morning' ? 'inset-x-0 top-0 h-1/2'
                  : selPart === 'afternoon' ? 'inset-x-0 bottom-0 h-1/2'
                  : 'inset-0'; // intera / orario → centro
                return (
                  <div className={`absolute ${pos} flex items-center justify-center z-10 pointer-events-none`}>
                    <span className="text-[10px] md:text-xs font-black leading-none text-violet-800">
                      &euro;{Math.round(shown)}
                    </span>
                  </div>
                );
              })()}

              {/* Price: giornata intera + mezza (½) quando configurata — celle NON selezionate */}
              {showPrices && isCurrentMonth && !isPast && price != null && !(violet && (isStart || isEnd || inRange)) && (
                <div className="mt-auto relative z-10">
                  <span className={`text-[10px] md:text-xs font-black leading-none ${
                    isStart || isEnd ? 'text-white/90'
                    : inRange ? 'text-blue-600'
                    : override?.price != null ? 'text-blue-600'
                    : 'text-slate-400'
                  }`}>
                    &euro;{Math.round(price)}
                  </span>
                  {override?.halfPrice != null && override.halfPrice > 0 && (
                    <span title="Mezza giornata" className={`block text-[8px] md:text-[9px] font-bold leading-tight ${
                      isStart || isEnd ? 'text-white/70' : inRange ? 'text-blue-400' : 'text-slate-400/80'
                    }`}>
                      &frac12; &euro;{Math.round(override.halfPrice)}
                    </span>
                  )}
                </div>
              )}

              {/* Price loading skeleton */}
              {showPrices && isCurrentMonth && !isPast && priceLoading && price == null && (
                <div className="mt-auto relative z-10">
                  <div className="h-3 w-8 bg-slate-200 rounded animate-pulse" />
                </div>
              )}

              {/* Slot indicators (small dots) */}
              {showPrices && isPartial && isCurrentMonth && !isStart && !isEnd && (
                <div className="flex gap-0.5 mt-0.5 relative z-10">
                  <span className={`w-1 h-1 rounded-full ${slots!.morning ? 'bg-emerald-400' : 'bg-red-400'}`}
                    title={slots!.morning ? t('datePicker.morningFree') : t('datePicker.morningBooked')} />
                  <span className={`w-1 h-1 rounded-full ${slots!.afternoon ? 'bg-emerald-400' : 'bg-red-400'}`}
                    title={slots!.afternoon ? t('datePicker.afternoonFree') : t('datePicker.afternoonBooked')} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
